import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import classNames from 'classnames';
import { SegmentControl, SegmentControlItem } from '@finn-no/troika-react-segment-control';

import { serverDate } from 'shared/locale/datetime.js';
import { storeLastSearch } from 'shared/flight/defaultState.js';
import { trackSearch } from '../tracking/trackSearch.js';
import validateSearchForm from './validation.js';
import actions from '../state/actions.js';
import formActions from '../state/formActions.js';
import constants from '../util/constants.js';

import Autocomplete from 'shared/autocomplete/Autocomplete.jsx';
import SingleDateCalendar from 'shared/calendar/SingleDateCalendar.jsx';
import DateRangeCalendar from 'shared/calendar/DateRangeCalendar.jsx';
import FlightPassengers from 'shared/passengers/FlightPassengers.jsx';

const common = 'form-grid__unit form-grid__unit--100-lt768';
const classNameList = {
    formGrid: common,
    outbound: `${common} input input--date`,
    inbound: `${common} input input--date`,
    dateRange: `${common} `,
    passengers: `${common} input input--select select-box`,
    cabinType: `${common} input input--select select-box`,
};

const ignoredFilters = [
    constants.DEPARTURE_AIRPORTS_LEG_ONE,
    constants.DEPARTURE_AIRPORTS_LEG_TWO,
    constants.ARRIVAL_AIRPORTS_LEG_ONE,
    constants.ARRIVAL_AIRPORTS_LEG_TWO,
    // TODO REIS-3620: Temporary ignoring time-filters (bugged)
    `${constants.OUTBOUND_DEPARTURE_TIME}From`,
    `${constants.OUTBOUND_DEPARTURE_TIME}To`,
    `${constants.OUTBOUND_ARRIVAL_TIME}From`,
    `${constants.OUTBOUND_ARRIVAL_TIME}To`,
    `${constants.INBOUND_DEPARTURE_TIME}From`,
    `${constants.INBOUND_DEPARTURE_TIME}To`,
    `${constants.INBOUND_ARRIVAL_TIME}From`,
    `${constants.INBOUND_ARRIVAL_TIME}To`,
];

const onewayIgnoredFilters = [
    `${constants.INBOUND_DEPARTURE_TIME}From`,
    `${constants.INBOUND_DEPARTURE_TIME}To`,
    `${constants.INBOUND_ARRIVAL_TIME}From`,
    `${constants.INBOUND_ARRIVAL_TIME}To`,
];

export class SearchForm extends React.Component {
    constructor(props) {
        super(props);

        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    static mapAutocompleteResults(res) {
        return res.map(opt => ({
            id: opt.value,
            text: opt.text,
            cls: opt.indent === 'true' ? 'u-pl32' : '',
            data: opt,
        }));
    }

    handleCancelClick(event) {
        event.preventDefault();
        this.props.dispatch(actions.toggleSearchForm());
    }

    handleSubmit(event) {
        this.props.dispatch(formActions.setPristine(false));

        if (!validateSearchForm(this.props.data)) {
            event.preventDefault();
            return false;
        }

        storeLastSearch(this.props.data);

        trackSearch(this.props.data);
        return true;
    }

    openJawHighlighter(date) {
        return this.props.data.isMulticity && this.props.data.departureDate && this.props.data.returnDate
            ? date.isBetween(this.props.data.departureDate, this.props.data.returnDate, 'day', '[]')
            : date.isSame(this.props.data.departureDate, 'day') || date.isSame(this.props.data.returnDate, 'day');
    }

    renderFilterInputs() {
        return Object.keys(this.props.filters)
            .filter(key => !ignoredFilters.includes(key) && !(this.props.data.isOneway && onewayIgnoredFilters.includes(key)))
            .map(key => <input key={`filter${key}`} type="hidden" name={key} value={this.props.filters[key]} />);
    }

    render() {
        const autocompleteUrl = `${FINN.travel.contextPath}autocomplete?destination=`;
        const { data, isResultPage, toggleSearch, t, dispatch } = this.props;
        const invalid = {
            origin: !data.pristine && !data.origin,
            destination: !data.pristine && !data.destination,
            origin2: !data.pristine && !data.origin2,
            destination2: !data.pristine && !data.destination2,
            departureDate: !data.pristine && !data.departureDate,
            returnDate: !data.pristine && !data.returnDate,
        };
        const showCalendarError = invalid.departureDate || invalid.returnDate;
        return (
            <form
                action={FINN.travel.contextPath + FINN.travel.actionUrl}
                data-testid="flightSearchForm"
                id="flightSearchForm"
                className="resultSearchForm u-ma0"
                method="POST"
                autoComplete="off"
                onSubmit={this.handleSubmit}>
                <h2 className="u-screen-reader-only">{t('searchTitle')}</h2>
                <input type="hidden" id="tripType" name="tripType" data-testid="hiddenTripType" value={data.tripType} />
                <SegmentControl
                    name="tripTypeControl"
                    value={data.tripType}
                    onChange={event => this.props.dispatch(formActions.setTripType(event.target.value))}
                    small
                    justified
                    className={`u-r-size1of${isResultPage ? 2 : 3} u-pb32`}
                    aria-label={this.props.t('tripType.title')}>
                    <SegmentControlItem value="roundtrip" data-testid="roundtrip" className="u-bg-milk">
                        {this.props.t('tripType.roundtrip')}
                    </SegmentControlItem>
                    <SegmentControlItem value="oneway" data-testid="oneway" className="u-bg-milk">
                        {this.props.t('tripType.oneway')}
                    </SegmentControlItem>
                    <SegmentControlItem value="openjaw" data-testid="openjaw" className="u-bg-milk">
                        {this.props.t('tripType.openjaw')}
                    </SegmentControlItem>
                </SegmentControl>
                <div className="form-grid" style={{ alignItems: 'baseline', justifyContent: 'center' }}>
                    <div className={classNameList.formGrid}>
                        <Autocomplete
                            id="origin"
                            placeholder={t('autocomplete.from')}
                            value={data.originText}
                            baseUrl={autocompleteUrl}
                            mapResults={SearchForm.mapAutocompleteResults}
                            errorText={invalid.origin ? t('validation.origin') : ''}
                            onChange={value => dispatch(formActions.setAutocomplete({ field: 'origin', value }))}
                            onChangeText={value => dispatch(formActions.setAutocompleteText({ field: 'origin', value }))}
                        />
                        <input type="hidden" name="requestedOrigin" value={data.origin} />
                    </div>
                    <div className={classNameList.formGrid}>
                        <Autocomplete
                            id="destination"
                            placeholder={t('autocomplete.to')}
                            value={data.destinationText}
                            baseUrl={autocompleteUrl}
                            mapResults={SearchForm.mapAutocompleteResults}
                            errorText={invalid.destination ? t('validation.destination') : ''}
                            onChange={value => dispatch(formActions.setAutocomplete({ field: 'destination', value }))}
                            onChangeText={value => dispatch(formActions.setAutocompleteText({ field: 'destination', value }))}
                        />
                        <input type="hidden" name="requestedDestination" value={data.destination} />
                    </div>
                    {data.isOneway || data.isMulticity ? (
                        <div className={classNames(classNameList.outbound, { 'input--is-invalid': invalid.departureDate })}>
                            <label htmlFor="requestedDepartureDate">{t('common:flight.outbound')}</label>
                            <SingleDateCalendar
                                id="requestedDepartureDate"
                                value={data.departureDate}
                                onFocusChange={() => {}}
                                onDateChange={newDate => dispatch(formActions.setDepartureDate(newDate))}
                                onDateReset={newDate => dispatch(formActions.setDepartureDate(newDate))}
                                isDayHighlighted={this.openJawHighlighter.bind(this)}
                                anchorDirection="right"
                                showCalendar
                            />
                            {showCalendarError ? (
                                <div className="input__sub-text">
                                    {invalid.departureDate ? (
                                        <span className="input__sub-text__error">{t('validation.departureDate')}</span>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div
                            className={classNames(classNameList.dateRange, {
                                'input--is-invalid': invalid.departureDate || invalid.returnDate,
                            })}>
                            <div className="input input--date calendar-special">
                                <label className="date-range-label date-range-start" htmlFor="requestedDepartureDate">
                                    {t('common:flight.outbound')}
                                </label>
                                <label className="date-range-label date-range-end" htmlFor="requestedReturnDate">
                                    {t('common:flight.inbound')}
                                </label>
                                <DateRangeCalendar
                                    startId="requestedDepartureDate"
                                    endId="requestedReturnDate"
                                    startPlaceholder=""
                                    endPlaceholder=""
                                    startDate={data.departureDate}
                                    endDate={data.returnDate}
                                    anchorDirection="right"
                                    onFocusChange={() => {}}
                                    onStartDateChange={newDate => dispatch(formActions.setDepartureDate(newDate))}
                                    onEndDateChange={newDate => dispatch(formActions.setReturnDate(newDate))}
                                    resetDepartureDate={newDate => dispatch(formActions.setDepartureDate(newDate))}
                                    resetReturnDate={newDate => dispatch(formActions.setReturnDate(newDate))}
                                    showCalendar
                                />
                                {showCalendarError ? (
                                    <div className="input__sub-text u-size1of1">
                                        <div className="input__sub-text__error u-truncate date-range-error date-range-start">
                                            {invalid.departureDate ? t('validation.departureDate') : null}
                                        </div>
                                        <div className="input__sub-text__error u-truncate date-range-error date-range-end">
                                            {invalid.returnDate ? t('validation.returnDate') : null}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
                {data.isMulticity && (
                    <div className="form-grid">
                        <div className={classNameList.formGrid}>
                            <Autocomplete
                                id="origin2"
                                placeholder={t('autocomplete.from')}
                                value={data.originText2}
                                baseUrl={autocompleteUrl}
                                mapResults={SearchForm.mapAutocompleteResults}
                                errorText={invalid.origin2 ? t('validation.origin') : ''}
                                onChange={value => dispatch(formActions.setAutocompleteMultiCity({ field: 'origin', value }))}
                                onChangeText={value => dispatch(formActions.setAutocompleteTextMultiCity({ field: 'origin', value }))}
                            />
                            <input type="hidden" name="requestedOrigin2" value={data.origin2} />
                        </div>
                        <div className={classNameList.formGrid}>
                            <Autocomplete
                                id="destination2"
                                placeholder={t('autocomplete.to')}
                                value={data.destinationText2}
                                baseUrl={autocompleteUrl}
                                mapResults={SearchForm.mapAutocompleteResults}
                                errorText={invalid.destination2 ? t('validation.destination') : ''}
                                onChange={value => dispatch(formActions.setAutocompleteMultiCity({ field: 'destination', value }))}
                                onChangeText={value => dispatch(formActions.setAutocompleteTextMultiCity({ field: 'destination', value }))}
                            />
                            <input type="hidden" name="requestedDestination2" value={data.destination2} />
                        </div>
                        <div className={classNames(classNameList.inbound, { 'input--is-invalid': invalid.returnDate })}>
                            <label htmlFor="requestedReturnDate">{t('common:flight.inbound')}</label>
                            <SingleDateCalendar
                                id="requestedReturnDate"
                                value={data.returnDate}
                                onFocusChange={() => {}}
                                onDateChange={newDate => dispatch(formActions.setReturnDate(newDate))}
                                onDateReset={newDate => dispatch(formActions.setReturnDate(newDate))}
                                isDayHighlighted={this.openJawHighlighter.bind(this)}
                                anchorDirection="right"
                                showCalendar
                            />
                            {showCalendarError ? (
                                <div className="input__sub-text">
                                    {invalid.returnDate ? (
                                        <span className="input__sub-text__error">{t('validation.returnDate')}</span>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}
                <div className="form-grid">
                    <div className="form-grid__unit input input--select select-box" data-testid="flightPassengers">
                        <label htmlFor="flightPassengers">{t('passengers.title')}</label>
                        <div className="input--select__wrap">
                            <FlightPassengers
                                id="flightPassengers"
                                adults={data.adults}
                                children={data.children}
                                addAdult={() => dispatch(formActions.addAdult())}
                                removeAdult={() => dispatch(formActions.removeAdult())}
                                setAdults={count => dispatch(formActions.setAdults(count))}
                                addChild={() => dispatch(formActions.addChild())}
                                removeChild={() => dispatch(formActions.removeChild())}
                                setChildren={count => dispatch(formActions.setChildren(count))}
                                increaseChildAge={idx => dispatch(formActions.increaseChildAge(idx))}
                                decreaseChildAge={idx => dispatch(formActions.decreaseChildAge(idx))}
                                setChildAge={childData => dispatch(formActions.setChildAge(childData))}
                                showPassengerWarning={data.showPassengerWarning}
                            />
                        </div>
                        <input type="hidden" name="numberOfAdults" value={data.adults} />
                        {data.children.map((age, idx) => (
                            <input type="hidden" name="childAges" value={age} key={`childAge${idx}`} />
                        ))}
                    </div>
                    <div className="form-grid__unit input input--select select-box">
                        <label htmlFor="cabinType">{t('cabinType.title')}</label>
                        <div className="input--select__wrap">
                            <select
                                id="cabinType"
                                name="cabinType"
                                data-testid="cabinType"
                                className="input-bg"
                                aria-label={t('cabinType.title')}
                                value={data.cabinType}
                                onChange={e => dispatch(formActions.setCabinType(e.target.value))}>
                                <option value="economy">{t('cabinType.economy')}</option>
                                <option value="premium">{t('cabinType.premium')}</option>
                                <option value="business">{t('cabinType.business')}</option>
                                <option value="first">{t('cabinType.first')}</option>
                                <option value="economy,premium,business,first">{t('cabinType.all')}</option>
                            </select>
                        </div>
                    </div>
                </div>
                {this.renderFilterInputs()}
                <div className="grid">
                    <button
                        type="submit"
                        className={classNames('grid__unit button button--cta', {
                            'u-ph32 u-pv8': isResultPage,
                            'u-r-size1of6': !isResultPage,
                        })}
                        id="flightSearchButton">
                        {t('common:action.search')}
                    </button>
                    {toggleSearch ? (
                        <div className="u-float-right u-pv8">
                            <a href="#" id="cancelSearch" className="u-pa16" role="button" onClick={this.handleCancelClick}>
                                {t('common:action.cancel')}
                            </a>
                        </div>
                    ) : null}
                </div>
            </form>
        );
    }
}

SearchForm.propTypes = {
    t: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    toggleSearch: PropTypes.func,
    isResultPage: PropTypes.bool,
};

SearchForm.defaultProps = {
    isResultPage: false,
};

const mapStateToProps = state => {
    const departureDate = state.searchForm.departureDate && serverDate(state.searchForm.departureDate);
    const returnDate = state.searchForm.returnDate && serverDate(state.searchForm.returnDate);
    return { data: Object.assign(state.searchForm, { departureDate, returnDate }), filters: state.filterState };
};
export default connect(mapStateToProps)(withTranslation('search')(SearchForm));
